import { useEffect, useState } from "react";
import { api, useAuth, userModules } from "@/context/AuthContext";
import { LOKASI_OPTIONS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import ChipMultiFilter from "@/components/summary/ChipMultiFilter";
import KeuanganSummaryCard from "@/components/summary/KeuanganSummaryCard";
import AreaSummaryCard from "@/components/summary/AreaSummaryCard";
import AssetSummaryCard from "@/components/summary/AssetSummaryCard";
import BirthdayCard from "@/components/summary/BirthdayCard";
import PensiunReminderCard from "@/components/summary/PensiunReminderCard";
import LowStockCard from "@/components/summary/LowStockCard";
import LemburSummaryCard from "@/components/summary/LemburSummaryCard";
import { Loader2, SlidersHorizontal, ArrowRight } from "lucide-react";
import PekerjaCharts from "@/components/summary/PekerjaCharts";
import useChartPref from "@/hooks/useChartPref";

export default function SummaryView({ onNavigate }) {
  const { user } = useAuth();
  const mods = userModules(user);
  const hasPekerja = mods.includes("pekerja");
  const canConsumable = mods.includes("barang");
  const [data, setData] = useState(null);
  const [fLokasi, setFLokasi] = useState([]);
  const [fJabatan, setFJabatan] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lokasiOptions, setLokasiOptions] = useState(LOKASI_OPTIONS);
  const [tLokasi, setTLokasi] = useChartPref("lokasi");
  const [tJabatan, setTJabatan] = useChartPref("jabatan");

  useEffect(() => {
    if (!hasPekerja) return;
    api.get("/lokasi").then((res) => setLokasiOptions(res.data)).catch(() => {});
  }, [hasPekerja]);

  useEffect(() => {
    if (!hasPekerja) return;
    setRefreshing(true);
    const params = {};
    if (fLokasi.length) params.lokasi = fLokasi.join(",");
    if (fJabatan.length) params.jabatan = fJabatan.join(",");
    api.get("/summary", { params })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setRefreshing(false));
  }, [fLokasi, fJabatan, hasPekerja]);

  if (hasPekerja && !data) {
    return (
      <div className="h-64 flex items-center justify-center" data-testid="summary-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="summary-view">
      {mods.includes("keuangan") && <KeuanganSummaryCard onNavigate={onNavigate} />}
      {mods.includes("area") && <AreaSummaryCard onNavigate={onNavigate} />}
      {mods.includes("asset") && <AssetSummaryCard onNavigate={onNavigate} />}

      {hasPekerja && data && (<>
      <div className="flex flex-wrap items-center gap-2" data-testid="summary-filter-bar">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
          <SlidersHorizontal className="h-4 w-4 text-blue-600" /> Saring statistik:
        </div>
        <ChipMultiFilter label="Lokasi" options={lokasiOptions} value={fLokasi} onChange={setFLokasi} testId="summary-filter-lokasi" />
        <ChipMultiFilter label="Jabatan" options={data.jabatan_options || []} value={fJabatan} onChange={setFJabatan} testId="summary-filter-jabatan" />
        {(fLokasi.length > 0 || fJabatan.length > 0) && (
          <Button variant="ghost" onClick={() => { setFLokasi([]); setFJabatan([]); }} data-testid="summary-filter-reset"
            className="h-10 text-slate-500 hover:text-rose-600">Reset</Button>
        )}
        {refreshing && <Loader2 className="h-4 w-4 animate-spin text-blue-600" data-testid="summary-refreshing" />}
      </div>

      <PekerjaCharts data={data} onNavigate={onNavigate} tLokasi={tLokasi} setTLokasi={setTLokasi} tJabatan={tJabatan} setTJabatan={setTJabatan} />

      {mods.includes("lembur") && <LemburSummaryCard onNavigate={onNavigate} lokasi={fLokasi.join(",") || "all"} />}

      <PensiunReminderCard reminders={data.pensiun_reminders} onNavigate={onNavigate} />
      <BirthdayCard birthdays={data.birthdays_this_month} onNavigate={onNavigate} />
      </>)}
      <LowStockCard canConsumable={canConsumable} onNavigate={onNavigate} />
    </div>
  );
}
